import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from '../../store/slices/authSlice';
import { initializeOpenAPI } from '../../config/openapi';
import logo from '../../assets/img/Logo.png';
import CloseMenuIcon from '../Icons/CloseMenuIcon';
import HamburgerIcon from '../Icons/HamburgerIcon';
import HeaderToolbar from './HeaderToolbar';
import { useEffect, useMemo } from 'react';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/redux';
import { fetchContentMasterAiTokens } from '../../store/slices/contentMasterSlice';

interface HeaderProps {
  isLoginHeader: boolean;
  showLoginHeader: boolean;
  onMenuToggle?: () => void;
  isMobileMenuOpen?: boolean;
  onProfileClick?: () => void;
}

export const Header = ({
  isLoginHeader,
  showLoginHeader,
  onMenuToggle,
  isMobileMenuOpen = false,
  onProfileClick,
}: HeaderProps) => {
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const aiTokensSummary = useSelector((state: RootState) => state.contentMaster.aiTokensSummary);
  const aiTokensLoading = useSelector((state: RootState) => state.contentMaster.aiTokensLoading);

  const isOnContentMaster = location.pathname.startsWith('/features/content-master');

  const headerTokensBadge = useMemo<{ label: string; tone: 'default' | 'warning' | 'danger' } | null>(() => {
    if (!aiTokensSummary) {
      return null;
    }
    const allowed = aiTokensSummary.totalAllowedTokens;
    const remaining = aiTokensSummary.tokensRemaining;
    const overage = aiTokensSummary.overageTokens ?? 0;
    const onDemand = aiTokensSummary.onDemandLimitTokens ?? 0;
    if (allowed == null || remaining == null) {
      return null;
    }
    const used = Math.max(0, allowed - remaining) + overage;
    const totalAvailable = allowed + onDemand;
    if (totalAvailable <= 0) {
      return null;
    }
    const usedPct = Math.min(100, (used / totalAvailable) * 100);
    const remainingPct = Math.max(0, 100 - usedPct);
    const rounded = Math.round(remainingPct);
    let tone: 'default' | 'warning' | 'danger' = 'default';
    if (remainingPct < 10) {
      tone = 'danger';
    } else if (remainingPct < 20) {
      tone = 'warning';
    }
    return {
      label: `${rounded}%`,
      tone,
    };
  }, [aiTokensSummary]);

  useEffect(() => {
    if (isLoginHeader || !isOnContentMaster) {
      return;
    }
    if (aiTokensSummary != null || aiTokensLoading) {
      return;
    }
    appDispatch(fetchContentMasterAiTokens(undefined));
  }, [appDispatch, isOnContentMaster, isLoginHeader, aiTokensSummary, aiTokensLoading]);

  const handleLogout = async () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      initializeOpenAPI(undefined);
      dispatch(logout());
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  return (
    <header className="top-header">
      <div className="header-left">
        {showLoginHeader && (
          <div className="header-logo">
            <img src={logo} alt={t('logo_alt')} className="logo" />
          </div>
        )}
        {!isLoginHeader && onMenuToggle && (
          <>
            <div className="header-logo header-logo-mobile">
              <img src={logo} alt={t('logo_alt')} className="logo" />
            </div>
            <button
              type="button"
              className="mobile-menu-close menu-toggle"
              onClick={onMenuToggle}
              aria-label={isMobileMenuOpen ? t('sidebar_close_menu') : t('menu')}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <CloseMenuIcon /> : <HamburgerIcon />}
            </button>
          </>
        )}
      </div>
      <div className="header-right">
        {!isLoginHeader && isOnContentMaster && headerTokensBadge != null && (
          <Link
            to="/features/content-master/usage"
            className={
              headerTokensBadge.tone === 'danger'
                ? 'content-master-header-tokens-badge content-master-header-tokens-badge--danger'
                : headerTokensBadge.tone === 'warning'
                  ? 'content-master-header-tokens-badge content-master-header-tokens-badge--warning'
                  : 'content-master-header-tokens-badge'
            }
            aria-label={t('content_master_header_remaining_tokens', { pct: headerTokensBadge.label })}
          >
            <svg
              className="content-master-header-tokens-badge__icon"
              width={16}
              height={16}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
              focusable="false"
            >
              <path
                d="M4.48901 11.2316L10.0782 2.44859C10.3467 2.02657 11 2.2168 11 2.71703V8.9C11 8.95523 11.0448 9 11.1 9H15.0397C15.4442 9 15.6813 9.45534 15.4493 9.78673L9.90962 17.7005C9.62928 18.101 9 17.9027 9 17.4138V12.1C9 12.0448 8.95523 12 8.9 12H4.91084C4.51629 12 4.27718 11.5644 4.48901 11.2316Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
            <span className="content-master-header-tokens-badge__text">
              {t('content_master_header_remaining_tokens', { pct: headerTokensBadge.label })}
            </span>
          </Link>
        )}
        <HeaderToolbar
          isLoginHeader={isLoginHeader}
          onLogout={handleLogout}
          onProfileClick={onProfileClick}
        />
      </div>
    </header>
  );
};
